import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import db from "../Firebase";
import { collection, onSnapshot, deleteDoc, doc } from "firebase/firestore";
import { getStorage, ref, deleteObject } from "firebase/storage";

function ReadymadePage() {
    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [types, setTypes] = useState([]);
    const [selectedType, setSelectedType] = useState("All");
    const [imageDims, setImageDims] = useState({});
    const [vcSamples, setVcSamples] = useState([]);
    const [deleting, setDeleting] = useState(false);

    const storage = getStorage();

    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "nv_saved_draft"),
            (snapshot) => {
                const data = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setItems(data);
                setFilteredItems(data.filter((i) => i.type !== "DC"));

                const uniqueTypes = [
                    ...new Set(
                        data
                            .map((item) => item.type)
                            .filter((type) => type && type.trim() !== "" && type !== "DC")
                    ),
                ];

                setTypes(uniqueTypes);
            }
        );

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "nv_visiting_card_samples"),
            (snapshot) => {
                const data = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setVcSamples(data);
            }
        );
        return () => unsubscribe();
    }, []);

    const handleFilter = (type) => {
        setSelectedType(type);

        if (type === "All") {
            setFilteredItems(items.filter((item) => item.type !== "DC"));
        } else if (type === "DC") {
            setFilteredItems([]);
        } else {
            setFilteredItems(items.filter((item) => item.type === type));
        }
    };

    const intToHexColor = (intColor) => {
        if (!intColor) return "#000000";
        const hex = (intColor & 0xffffff).toString(16).padStart(6, "0");
        return `#${hex}`;
    };

    const handleImageLoad = (e, id) => {
        setImageDims((prev) => ({
            ...prev,
            [id]: { w: e.target.naturalWidth, h: e.target.naturalHeight },
        }));
    };

    const handleDeleteVc = async (id, bgUrl) => {
        if (deleting) return;

        const confirm = await Swal.fire({
            title: "Delete DC Template?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
        });

        if (!confirm.isConfirmed) return;

        setDeleting(true);
        try {
            await deleteDoc(doc(db, "nv_visiting_card_samples", id));

            if (bgUrl) {
                try {
                    const imageRef = ref(storage, bgUrl);
                    await deleteObject(imageRef);
                } catch (_) {}
            }

            Swal.fire("Deleted!", "DC template removed.", "success");
        } catch (error) {
            Swal.fire("Error", "Delete failed!", "error");
        } finally {
            setDeleting(false);
        }
    };

    const handleDelete = async (id, bgImage) => {
        if (deleting) return;

        const confirm = await Swal.fire({
            title: "Delete Template?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
        });

        if (!confirm.isConfirmed) return;

        setDeleting(true);
        try {
            await deleteDoc(doc(db, "nv_saved_draft", id));

            if (bgImage) {
                const imageRef = ref(storage, bgImage);
                await deleteObject(imageRef);
            }

            Swal.fire("Deleted!", "Template removed.", "success");
        } catch (error) {
            Swal.fire("Error", "Delete failed!", "error");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            {/* HEADER */}
            <div className="bg-dark text-white py-3 px-4 d-flex justify-content-between align-items-center shadow-sm">
                <h5 className="mb-0 fw-bold">✨ Readymade Templates</h5>
                <Link to="/">
                    <img
                        src="/images/menubutton.png"
                        alt="Menu"
                        style={{ height: "2rem", filter: "invert(1)" }}
                    />
                </Link>
            </div>

            <div className="container py-4">

                {/* FILTER SECTION */}
                <div className="mb-4 text-center">
                    <button
                        className={`btn rounded-pill px-4 me-2 mb-2 ${selectedType === "All"
                            ? "btn-dark shadow"
                            : "btn-outline-dark"
                            }`}
                        onClick={() => handleFilter("All")}
                    >
                        All
                    </button>

                    {types.map((type) => (
                        <button
                            key={type}
                            className={`btn rounded-pill px-4 me-2 mb-2 ${selectedType === type
                                ? "btn-dark shadow"
                                : "btn-outline-dark"
                                }`}
                            onClick={() => handleFilter(type)}
                        >
                            {type}
                        </button>
                    ))}

                    {vcSamples.length > 0 && (
                        <button
                            className={`btn rounded-pill px-4 me-2 mb-2 ${selectedType === "DC"
                                ? "btn-dark shadow"
                                : "btn-outline-dark"
                                }`}
                            onClick={() => handleFilter("DC")}
                        >
                            DC
                        </button>
                    )}
                </div>

                {/* EMPTY STATE */}
                {filteredItems.length === 0 && !(selectedType === "DC" && vcSamples.length > 0) && (
                    <div className="text-center text-muted py-5">
                        <h6>No Templates Found</h6>
                    </div>
                )}

                {/* GRID */}
                <div className="row">
                    {filteredItems.map((item) => (
                        <div className="col-lg-4 col-md-6 mb-4" key={item.id}>
                            {(() => {
                                const t = item.type;
                                const previewW = t === "Story" ? 270 : t === "DC" ? 380 : t === "einvite" ? 270 : 340;
                                const previewH = t === "Story" ? 480 : t === "DC" ? 200 : t === "einvite" ? 480 : 340;
                                const dims = imageDims[item.id];
                                const canvasW = dims ? dims.w : previewW;
                                const canvasH = dims ? dims.h : previewH;
                                const scale = dims ? Math.min(previewW / canvasW, previewH / canvasH) : 1;
                                return (
                                    <div
                                        className="card border-0 shadow-lg"
                                        style={{ borderRadius: "18px", transition: "0.3s ease" }}
                                    >
                                        {/* PREVIEW CLIP BOX */}
                                        <div
                                            style={{
                                                position: "relative",
                                                width: `${previewW}px`,
                                                height: `${previewH}px`,
                                                overflow: "hidden",
                                                borderRadius: "18px 18px 0 0",
                                                background: "#f8f9fa",
                                            }}
                                        >
                                            {/* INNER CANVAS — natural size, scaled as one unit */}
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: `${canvasW}px`,
                                                    height: `${canvasH}px`,
                                                    transform: `scale(${scale})`,
                                                    transformOrigin: "top left",
                                                }}
                                            >
                                                <img
                                                    src={item.backgroundImage}
                                                    alt=""
                                                    onLoad={(e) => handleImageLoad(e, item.id)}
                                                    style={{
                                                        display: "block",
                                                        width: `${canvasW}px`,
                                                        height: `${canvasH}px`,
                                                    }}
                                                />

                                                {/* Overlay */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        background: "linear-gradient(to top, rgba(0,0,0,0.5), transparent)",
                                                        pointerEvents: "none",
                                                    }}
                                                />

                                                {/* Stickers */}
                                                {item.stickerElements &&
                                                    item.stickerElements.map((sticker, index) => (
                                                        <img
                                                            key={index}
                                                            src={`/${sticker.imagePath}`}
                                                            alt=""
                                                            style={{
                                                                position: "absolute",
                                                                left: `${sticker.positionX}px`,
                                                                top: `${sticker.positionY}px`,
                                                                width: "auto",
                                                                height: "auto",
                                                                transform: `scale(${sticker.scale})`,
                                                                transformOrigin: "top left",
                                                                pointerEvents: "none",
                                                            }}
                                                        />
                                                    ))}

                                            </div>

                                            {/* Text overlay — natural flow, no X/Y */}
                                            {item.textElements && item.textElements.length > 0 && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        padding: "10px",
                                                        gap: "6px",
                                                        pointerEvents: "none",
                                                    }}
                                                >
                                                    {item.textElements.map((textEl, index) => (
                                                        <div
                                                            key={index}
                                                            style={{
                                                                fontSize: "13px",
                                                                fontFamily: textEl.fontFamily || "Arial",
                                                                fontWeight: textEl.isBold ? "bold" : "normal",
                                                                fontStyle: textEl.isItalic ? "italic" : "normal",
                                                                color: intToHexColor(textEl.color),
                                                                textShadow: "0 1px 4px rgba(0,0,0,0.9)",
                                                                textAlign: "center",
                                                                wordBreak: "break-word",
                                                                maxWidth: "90%",
                                                            }}
                                                        >
                                                            {textEl.text}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Floating Delete — outside inner canvas so it stays at preview size */}
                                            <button
                                                onClick={() => handleDelete(item.id, item.backgroundImage)}
                                                disabled={deleting}
                                                className="btn btn-danger btn-sm position-absolute"
                                                style={{ top: "10px", right: "10px", borderRadius: "50px", zIndex: 10 }}
                                            >
                                                🗑
                                            </button>
                                        </div>

                                        {/* FOOTER */}
                                        <div className="card-body pb-1 d-flex justify-content-between align-items-center">
                                            <span className="badge bg-dark">
                                                {item.type || "N/A"}
                                            </span>
                                            <span className={`badge ${item.paid ? "bg-success" : "bg-secondary"}`}>
                                                {item.paid ? "Paid" : "Free"}
                                            </span>
                                        </div>

                                        {/* TEXT CONTENT LIST */}
                                        {item.textElements && item.textElements.length > 0 && (
                                            <div
                                                className="px-3 pb-3"
                                                style={{
                                                    maxHeight: "110px",
                                                    overflowY: "auto",
                                                    borderTop: "1px solid #f0f0f0",
                                                }}
                                            >
                                                {item.textElements.map((textEl, index) => (
                                                    <div
                                                        key={index}
                                                        className="d-flex align-items-start gap-2 py-1"
                                                        style={{ borderBottom: "1px dashed #eee" }}
                                                    >
                                                        <span
                                                            className="badge mt-1 flex-shrink-0"
                                                            style={{
                                                                background: intToHexColor(textEl.color),
                                                                border: "1px solid #ccc",
                                                                width: "12px",
                                                                height: "12px",
                                                                borderRadius: "2px",
                                                                padding: 0,
                                                            }}
                                                        />
                                                        <span
                                                            style={{
                                                                fontSize: "12px",
                                                                fontFamily: textEl.fontFamily || "Arial",
                                                                fontWeight: textEl.isBold ? "bold" : "normal",
                                                                fontStyle: textEl.isItalic ? "italic" : "normal",
                                                                color: "#333",
                                                                wordBreak: "break-word",
                                                                lineHeight: "1.4",
                                                            }}
                                                        >
                                                            {textEl.text}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    ))}
                </div>
            </div>

            {/* VISITING CARD SAMPLES — DC FORMAT */}
            {vcSamples.length > 0 && (selectedType === "All" || selectedType === "DC") && (
                <div className="container pb-5">
                    <h6 className="fw-bold mb-3 border-bottom pb-2">Digital Card (DC)</h6>
                    <div className="row">
                        {vcSamples.map((vc) => {
                            const bgUrl = vc.backgroundImage || (Array.isArray(vc.bgUrls) ? vc.bgUrls[vc.bgIndex ?? 0] : null);
                            const previewW = 380;
                            const previewH = 200;
                            return (
                                <div className="col-lg-4 col-md-6 mb-4" key={vc.id}>
                                    <div className="card border-0 shadow-lg" style={{ borderRadius: "18px" }}>
                                        {/* DC PREVIEW */}
                                        <div
                                            style={{
                                                position: "relative",
                                                width: `${previewW}px`,
                                                height: `${previewH}px`,
                                                overflow: "hidden",
                                                borderRadius: "18px 18px 0 0",
                                                background: "#f0f0f0",
                                            }}
                                        >
                                            {bgUrl && (
                                                <img
                                                    src={bgUrl}
                                                    alt=""
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        display: "block",
                                                    }}
                                                />
                                            )}
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    background: "linear-gradient(to top, rgba(0,0,0,0.55), transparent)",
                                                    pointerEvents: "none",
                                                }}
                                            />
                                            {/* Delete button */}
                                            <button
                                                onClick={() => handleDeleteVc(vc.id, bgUrl)}
                                                disabled={deleting}
                                                className="btn btn-danger btn-sm position-absolute"
                                                style={{ top: "10px", right: "10px", borderRadius: "50px", zIndex: 10 }}
                                            >
                                                🗑
                                            </button>

                                            {/* Text overlay */}
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    bottom: 10,
                                                    left: 12,
                                                    right: 12,
                                                    color: vc.textColor || "#fff",
                                                    fontFamily: vc.fontFamily || "Arial",
                                                    pointerEvents: "none",
                                                }}
                                            >
                                                {vc.businessName && (
                                                    <div style={{ fontWeight: "bold", fontSize: "13px", textShadow: "0 1px 3px rgba(0,0,0,0.8)" }}>
                                                        {vc.businessName}
                                                    </div>
                                                )}
                                                {vc.ownerName && (
                                                    <div style={{ fontSize: "11px", textShadow: "0 1px 3px rgba(0,0,0,0.8)" }}>
                                                        {vc.ownerName}
                                                    </div>
                                                )}
                                                {(vc.phone || vc.phoneNumber) && (
                                                    <div style={{ fontSize: "11px", textShadow: "0 1px 3px rgba(0,0,0,0.8)" }}>
                                                        {vc.phone || vc.phoneNumber}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* FOOTER */}
                                        <div className="card-body py-2 d-flex justify-content-between align-items-center">
                                            <span className="badge bg-dark">DC</span>
                                            <span className={`badge ${vc.paid ? "bg-success" : "bg-secondary"}`}>
                                                {vc.paid ? "Paid" : "Free"}
                                            </span>
                                        </div>

                                        {/* DETAILS */}
                                        {(vc.email || vc.address) && (
                                            <div className="px-3 pb-3" style={{ fontSize: "12px", color: "#555", borderTop: "1px solid #f0f0f0" }}>
                                                {vc.email && <div className="pt-1">{vc.email}</div>}
                                                {vc.address && <div className="pt-1">{vc.address}</div>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
}

export default ReadymadePage;

// import React, { useEffect, useState } from "react";
// import { Link } from "react-router-dom";
// import Swal from "sweetalert2";
// import db from "../Firebase";
// import { collection, onSnapshot, deleteDoc, doc } from "firebase/firestore";
// import { getStorage, ref, deleteObject } from "firebase/storage";

// function ReadymadePage() {
//     const [items, setItems] = useState([]);
//     const [filteredItems, setFilteredItems] = useState([]);
//     const [types, setTypes] = useState([]);
//     const [selectedType, setSelectedType] = useState("All");

//     const storage = getStorage();

//     useEffect(() => {
//         const unsubscribe = onSnapshot(
//             collection(db, "nv_saved_draft"), // change if needed
//             (snapshot) => {
//                 const data = snapshot.docs.map((doc) => ({
//                     id: doc.id,
//                     ...doc.data(),
//                 }));

//                 setItems(data);
//                 setFilteredItems(data);

//                 // Extract Unique Types
//                 const uniqueTypes = [
//                     ...new Set(
//                         data
//                             .map((item) => item.type)
//                             .filter((type) => type && type.trim() !== "")
//                     ),
//                 ];

//                 setTypes(uniqueTypes);
//             }
//         );

//         return () => unsubscribe();
//     }, []);

//     // FILTER HANDLER
//     const handleFilter = (type) => {
//         setSelectedType(type);

//         if (type === "All") {
//             setFilteredItems(items);
//         } else {
//             const filtered = items.filter((item) => item.type === type);
//             setFilteredItems(filtered);
//         }
//     };

//     // Convert Flutter int color
//     const intToHexColor = (intColor) => {
//         if (!intColor) return "#000000";
//         const hex = (intColor & 0xffffff).toString(16).padStart(6, "0");
//         return `#${hex}`;
//     };

//     const handleDelete = async (id, bgImage) => {
//         const confirm = await Swal.fire({
//             title: "Delete?",
//             text: "This will permanently delete this template.",
//             icon: "warning",
//             showCancelButton: true,
//         });

//         if (!confirm.isConfirmed) return;

//         try {
//             await deleteDoc(doc(db, "nv_saved_draft", id));

//             if (bgImage) {
//                 const imageRef = ref(storage, bgImage);
//                 await deleteObject(imageRef);
//             }

//             Swal.fire("Deleted!", "Item removed.", "success");
//         } catch (error) {
//             console.error(error);
//             Swal.fire("Error", "Delete failed!", "error");
//         }
//     };

//     return (
//         <>
//             <div className="p-2 d-flex justify-content-between">
//                 <Link to="/">
//                     <img
//                         src="/images/menubutton.png"
//                         alt="Menu"
//                         style={{ height: "2rem" }}
//                     />
//                 </Link>
//             </div>

//             <div className="container mt-3">
//                 <h5>Readymade Templates ({items.length})</h5>

//                 {/* FILTER BUTTONS */}
//                 <div className="mb-3">
//                     <button
//                         className={`btn btn-sm ${selectedType === "All"
//                             ? "btn-primary"
//                             : "btn-outline-primary"
//                             } me-2`}
//                         onClick={() => handleFilter("All")}
//                     >
//                         All
//                     </button>

//                     {types.map((type) => (
//                         <button
//                             key={type}
//                             className={`btn btn-sm ${selectedType === type
//                                 ? "btn-primary"
//                                 : "btn-outline-primary"
//                                 } me-2`}
//                             onClick={() => handleFilter(type)}
//                         >
//                             {type}
//                         </button>
//                     ))}
//                 </div>

//                 {/* TEMPLATES LIST */}
//                 <div className="row">
//                     {filteredItems.map((item) => (
//                         <div className="col-md-4 mb-4" key={item.id}>
//                             <div className="card shadow-sm">
//                                 <div
//                                     style={{
//                                         position: "relative",
//                                         width: "100%",
//                                         height: "500px",
//                                         backgroundImage: `url(${item.backgroundImage})`,
//                                         backgroundSize: "cover",
//                                         backgroundPosition: "center",
//                                         overflow: "hidden",
//                                     }}
//                                 >
//                                     {/* Stickers */}
//                                     {item.stickerElements &&
//                                         item.stickerElements.map((sticker, index) => (
//                                             <img
//                                                 key={index}
//                                                 src={`/${sticker.imagePath}`}
//                                                 alt="sticker"
//                                                 style={{
//                                                     position: "absolute",
//                                                     left: `${sticker.positionX}px`,
//                                                     top: `${sticker.positionY}px`,
//                                                     transform: `scale(${sticker.scale})`,
//                                                     transformOrigin: "top left",
//                                                     pointerEvents: "none",
//                                                 }}
//                                             />
//                                         ))}

//                                     {/* Text */}
//                                     {item.textElements &&
//                                         item.textElements.map((textEl, index) => (
//                                             <div
//                                                 key={index}
//                                                 style={{
//                                                     position: "absolute",
//                                                     left: `${textEl.positionX}px`,
//                                                     top: `${textEl.positionY}px`,
//                                                     transform: `rotate(${textEl.rotation}deg)`,
//                                                     fontSize: `${textEl.fontSize}px`,
//                                                     fontFamily: textEl.fontFamily || "Arial",
//                                                     fontWeight: textEl.isBold ? "bold" : "normal",
//                                                     fontStyle: textEl.isItalic ? "italic" : "normal",
//                                                     color: intToHexColor(textEl.color),
//                                                     whiteSpace: "pre-wrap",
//                                                 }}
//                                             >
//                                                 {textEl.text}
//                                             </div>
//                                         ))}
//                                 </div>

//                                 <div className="card-body">
//                                     <p className="mb-1">
//                                         <strong>Type:</strong> {item.type || "N/A"}
//                                     </p>
//                                     <p className="mb-1">
//                                         <strong>Paid:</strong> {item.paid ? "Yes" : "No"}
//                                     </p>

//                                     <button
//                                         className="btn btn-danger btn-sm mt-2"
//                                         onClick={() =>
//                                             handleDelete(item.id, item.backgroundImage)
//                                         }
//                                     >
//                                         Delete
//                                     </button>
//                                 </div>
//                             </div>
//                         </div>
//                     ))}
//                 </div>
//             </div>
//         </>
//     );
// }

// export default ReadymadePage;
